import { format, isValid } from "date-fns";
import { ProgressNoteBy } from "enums/progress-noteBy";
import { SharedInteractionSnapshot } from "modules/charts-shared/utils/sharedSectionInteractions";
import { ChartProgressItem } from "types";
import { enumMapper } from "utils";

export const PROGRESS_SECTION_CODE = "SPGR";

type ProgressNoteRow = Partial<Omit<ChartProgressItem, "encounterId">> & {
  createdAt?: Date | string;
} & Record<string, unknown>;

const progressOptions = enumMapper.getOptions("chartProgressStatus");

function displayDate(value: unknown): string {
  if (!value || !(typeof value === "string" || value instanceof Date)) {
    return "";
  }
  const date = new Date(typeof value === "string" ? value : value.getTime());
  return isValid(date) ? format(date, "MM/dd/yyyy HH:mm") : "";
}

export function progressNotesCodePrefix(
  noteBy: ProgressNoteBy,
  isObservationNote: boolean
): string {
  return `ProgressNotes.${noteBy}.${
    isObservationNote ? "observation" : "progress"
  }.`;
}

export function progressNotesSnapshot(
  rows: ProgressNoteRow[],
  codePrefix: string
): SharedInteractionSnapshot {
  const snapshot: SharedInteractionSnapshot = {};
  rows.forEach((row) => {
    if (!row.id) return;
    const prefix = `${codePrefix}${row.id}`;
    // Notes are prepended on creation, so the label uses the creation time
    // instead of the row position to stay stable across reorders.
    const createdAt = displayDate(row.createdAt ?? row.date);
    const label = `${
      row.type === "transferCare" ? "Transfer Care" : "Progress Note"
    }${createdAt ? ` (${createdAt})` : ""}`;
    snapshot[`${prefix}.date`] = {
      label: `${label} - Date/Time`,
      value: displayDate(row.date),
    };
    if (row.type === "transferCare") {
      snapshot[`${prefix}.transferredTo`] = {
        label: `${label} - Transferred To`,
        value: row.transferredTo ?? "",
        elementType: "User",
      };
    } else {
      snapshot[`${prefix}.status`] = {
        label: `${label} - Status`,
        value: progressOptions
          .filter((option) => !!row[option.value])
          .map((option) => option.label)
          .join(", "),
      };
    }
    snapshot[`${prefix}.notes`] = {
      label: `${label} - Notes`,
      value: row.notes ?? "",
    };
    snapshot[`${prefix}.signedBy`] = {
      label: `${label} - Signed By`,
      value: row.signedBy ?? "",
      elementType: "User",
    };
  });
  return snapshot;
}
